import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../common/breadcrumb';
import ApiService, { API_URL } from '../services/api.service';
import AuthService from '../services/auth.service';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import moment from 'moment';
import Swal from 'sweetalert2';
import CancelChargeModal from './cancel-charge-modal';

const getVoucherUrl = (filename) => {
  const user = AuthService.getCurrentUser();
  if (!user || !user.token) return '';
  return `${API_URL}voucher/${filename}?token=${user.token}`;
};

const STATE_LABELS = {
  PENDIENTE_VERIFICACION: { label: 'Pendiente',  cls: 'badge-warning' },
  ACTIVO:                 { label: 'Aprobado',   cls: 'badge-success' },
  CANCELADO:              { label: 'Cancelado',  cls: 'badge-danger'  },
  ANULADO:                { label: 'Anulado',    cls: 'badge-dark'    },
};

// Un pago con deleted_at está anulado aunque su state diga otra cosa: los que
// se eliminaron antes de la anulación auditada conservan ACTIVO o PENDIENTE.
const isCancelled = (row) => !!row.deleted_at || row.state === 'ANULADO';

// Voucher aprobado cuyo dinero se aplicó entero a órdenes más antiguas del
// cliente (asignaciones); queda ACTIVO con valor 0.
const isFullyAllocated = (row) =>
  !isCancelled(row) && row.state === 'ACTIVO' && parseFloat(row.value || 0) === 0;

const personName = (u) => (u ? `${u.name || ''} ${u.last_name || ''}`.trim() : '');

const STATUS_CHARGE_LABELS = {
  'POR COBRAR': { label: 'Por cobrar', cls: 'badge-info'    },
  CANCELADO:    { label: 'Pagado',     cls: 'badge-success' },
  PARCIAL:      { label: 'Parcial',    cls: 'badge-warning' },
};

const StateBadge = ({ value, map }) => {
  const info = map[value] || { label: value || '—', cls: 'badge-secondary' };
  return <span className={`badge ${info.cls}`} style={{ fontSize: 12 }}>{info.label}</span>;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const INPUT_STYLE = { minHeight: 48, fontSize: 16 };
const BTN_STYLE   = { minHeight: 48, fontSize: 16 };

const PaymentsList = () => {
  const isMobile = useIsMobile();

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const today  = moment().format('YYYY-MM-DD');
  const last30 = moment().subtract(30, 'days').format('YYYY-MM-DD');

  const [filterState, setFilterState]       = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(last30);
  const [filterDateTo, setFilterDateTo]     = useState(today);
  const [filterSearch, setFilterSearch]     = useState('');
  const [inputSearch, setInputSearch]       = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const user = AuthService.getCurrentUser();
      const res  = await ApiService.getAllChargesAudit(user.token, {
        state:     filterState,
        date_from: filterDateFrom,
        date_to:   filterDateTo,
        search:    filterSearch,
      });
      setRows(res.data.data || []);
    } catch (e) {
      setError('Error al cargar los pagos. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [filterState, filterDateFrom, filterDateTo, filterSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilterSearch(inputSearch);
  };

  // Pago que se está anulando (abre el modal); null = modal cerrado
  const [chargeToCancel, setChargeToCancel] = useState(null);

  const handleCancelled = (cancelledCount) => {
    setChargeToCancel(null);
    const extra = cancelledCount > 1
      ? ` Se anularon ${cancelledCount} registros (incluye asignaciones).`
      : '';
    Swal.fire('Hecho', `Pago anulado.${extra}`, 'success');
    fetchData();
  };

  const handleReset = () => {
    setFilterState('');
    setFilterDateFrom(last30);
    setFilterDateTo(today);
    setFilterSearch('');
    setInputSearch('');
  };

  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      width: 60,
      show: !isMobile,
      Cell: ({ value }) => <span className="text-muted" style={{ fontSize: 12 }}>#{value}</span>,
    },
    {
      Header: '# Orden',
      accessor: 'id_purchase',
      width: isMobile ? 75 : 85,
      Cell: ({ value }) => (
        <Link to={`${process.env.PUBLIC_URL}/order-collections/${value}`} style={{ fontWeight: 600 }}>
          {value}
        </Link>
      ),
    },
    {
      Header: 'Cliente',
      id: 'cliente',
      accessor: row => {
        const u = row.Purchase && row.Purchase.User;
        return u ? `${u.name || ''} ${u.last_name || ''}`.trim() || '—' : '—';
      },
    },
    {
      Header: 'Estado pago',
      id: 'state',
      accessor: row => (isCancelled(row) ? 'ANULADO' : row.state),
      width: isMobile ? 90 : 115,
      Cell: ({ value }) => <StateBadge value={value} map={STATE_LABELS} />,
    },
    {
      Header: 'Estado orden',
      id: 'status_charge',
      width: 110,
      show: !isMobile,
      accessor: row => row.Purchase && row.Purchase.status_charge,
      Cell: ({ value }) => <StateBadge value={value} map={STATUS_CHARGE_LABELS} />,
    },
    {
      Header: 'Pagado',
      accessor: 'value',
      width: isMobile ? 80 : 105,
      Cell: ({ value, original }) => (
        <div>
          <strong>S/ {parseFloat(value || 0).toFixed(2)}</strong>
          {isFullyAllocated(original) && (
            <div className="text-muted" style={{ fontSize: 11, whiteSpace: 'normal' }}>
              aplicado a otras órdenes
            </div>
          )}
        </div>
      ),
    },
    {
      Header: 'Total',
      id: 'total_orden',
      width: 90,
      show: !isMobile,
      accessor: row => {
        const t = parseFloat((row.Purchase && row.Purchase.total)    || 0);
        const d = parseFloat((row.Purchase && row.Purchase.delivery) || 0);
        return (t + d).toFixed(2);
      },
      Cell: ({ value }) => `S/ ${value}`,
    },
    {
      Header: 'Diferencia',
      id: 'diferencia',
      width: isMobile ? 90 : 100,
      accessor: row => {
        if (isCancelled(row) || isFullyAllocated(row)) return null;
        if (row.Purchase && row.Purchase.status_charge === 'CANCELADO') return '0.00';
        const paid  = parseFloat(row.value || 0);
        const total = parseFloat((row.Purchase && row.Purchase.total)    || 0)
                    + parseFloat((row.Purchase && row.Purchase.delivery) || 0);
        return (total - paid).toFixed(2);
      },
      Cell: ({ value }) => (value === null ? <span className="text-muted">—</span> : (
        <span style={{ color: parseFloat(value) > 0.01 ? '#e74c3c' : '#27ae60', fontWeight: 600 }}>
          S/ {value}
        </span>
      )),
    },
    {
      Header: 'Fecha',
      id: 'fecha',
      // date_payment es solo fecha (DATEONLY): la hora sale de created_at, que
      // es cuándo se registró el pago. Si la fecha de pago que se ingresó es
      // otro día, se muestra debajo.
      accessor: row => row.created_at || row.date_payment,
      width: isMobile ? 100 : 135,
      Cell: ({ original }) => {
        const registered = original.created_at ? moment(original.created_at) : null;
        const paymentDay = original.date_payment ? moment(original.date_payment).format('YYYY-MM-DD') : null;
        if (!registered) return paymentDay || '—';
        return (
          <div style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
            {registered.format('YYYY-MM-DD HH:mm')}
            {paymentDay && paymentDay !== registered.format('YYYY-MM-DD') && (
              <div className="text-muted" style={{ fontSize: 11 }}>pago: {paymentDay}</div>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Fuente',
      accessor: 'source',
      width: 110,
      show: !isMobile,
      Cell: ({ value }) => <span className="text-muted" style={{ fontSize: 12 }}>{value || '—'}</span>,
    },
    {
      Header: 'Referencia',
      accessor: 'reference',
      show: !isMobile,
      Cell: ({ value }) => <span style={{ fontSize: 12 }}>{value || '—'}</span>,
    },
    {
      Header: 'Voucher',
      accessor: 'voucher_image',
      sortable: false,
      width: 64,
      Cell: ({ value }) =>
        value ? (
          <a href={getVoucherUrl(value)} target="_blank" rel="noopener noreferrer">
            <img
              src={getVoucherUrl(value)}
              alt="voucher"
              data-protected="1"
              style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </a>
        ) : <span className="text-muted">—</span>,
    },
    {
      Header: 'Anulación',
      id: 'anulacion',
      sortable: false,
      width: isMobile ? 110 : 170,
      Cell: ({ original }) => {
        if (!isCancelled(original)) {
          return (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              style={{ minHeight: 36 }}
              onClick={() => setChargeToCancel(original)}
            >
              Anular
            </button>
          );
        }
        const who  = personName(original.CancelledBy);
        const when = original.cancelled_at || original.deleted_at;
        return (
          <div style={{ fontSize: 12, lineHeight: 1.3, whiteSpace: 'normal' }} title={original.cancel_reason || ''}>
            <div>{who || 'Sin registro'}</div>
            {when && <div className="text-muted">{moment(when).format('DD/MM/YY HH:mm')}</div>}
            {original.cancel_reason && (
              <div className="text-muted" style={{ fontStyle: 'italic' }}>{original.cancel_reason}</div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Breadcrumb title="Pagos" parent="Órdenes" />
      <div className="container-fluid">

        {/* Filtros */}
        <div className="card mb-3">
          <div className="card-body py-3">
            <form onSubmit={handleSearch}>
              <div className="row">

                <div className="col-12 col-md-3 mb-2">
                  <label className="mb-1" style={{ fontSize: 14 }}>Estado del pago</label>
                  <select
                    className="form-control"
                    style={INPUT_STYLE}
                    value={filterState}
                    onChange={e => setFilterState(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="PENDIENTE_VERIFICACION">Pendiente verificación</option>
                    <option value="ACTIVO">Aprobado</option>
                    <option value="ANULADO">Anulado</option>
                  </select>
                </div>

                <div className="col-6 col-md-2 mb-2">
                  <label className="mb-1" style={{ fontSize: 14 }}>Desde</label>
                  <input
                    type="date"
                    className="form-control"
                    style={INPUT_STYLE}
                    value={filterDateFrom}
                    onChange={e => setFilterDateFrom(e.target.value)}
                  />
                </div>

                <div className="col-6 col-md-2 mb-2">
                  <label className="mb-1" style={{ fontSize: 14 }}>Hasta</label>
                  <input
                    type="date"
                    className="form-control"
                    style={INPUT_STYLE}
                    value={filterDateTo}
                    onChange={e => setFilterDateTo(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-3 mb-2">
                  <label className="mb-1" style={{ fontSize: 14 }}>Buscar cliente u orden</label>
                  <input
                    type="text"
                    className="form-control"
                    style={INPUT_STYLE}
                    placeholder="Nombre o # orden..."
                    value={inputSearch}
                    onChange={e => setInputSearch(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-2 mb-2 d-flex align-items-end">
                  <button type="submit" className="btn btn-primary mr-1 flex-grow-1" style={BTN_STYLE}>
                    Buscar
                  </button>
                  <button type="button" className="btn btn-light" style={{ ...BTN_STYLE, minWidth: 48 }} onClick={handleReset} title="Limpiar">
                    ✕
                  </button>
                </div>

              </div>
            </form>
          </div>
        </div>

        {/* Tabla */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between py-2">
            <h5 className="mb-0" style={{ fontSize: isMobile ? 15 : 17 }}>Historial de pagos</h5>
            <span className="text-muted" style={{ fontSize: 13 }}>{rows.length} registros</span>
          </div>
          <div className="card-body p-0">

            {loading && (
              <div className="text-center py-5">
                <span className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
                <p className="mt-3 text-muted">Cargando pagos...</p>
              </div>
            )}

            {!loading && error && (
              <div className="alert alert-danger mx-3 mt-3">{error}</div>
            )}

            {!loading && !error && (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <ReactTable
                  data={rows}
                  columns={columns}
                  defaultPageSize={25}
                  pageSizeOptions={[25, 50, 100]}
                  defaultSorted={[{ id: 'id', desc: true }]}
                  className="-striped -highlight"
                  style={{ fontSize: isMobile ? 13 : 13 }}
                  previousText="‹ Ant"
                  nextText="Sig ›"
                  loadingText="Cargando..."
                  noDataText="Sin resultados"
                  pageText="Pág."
                  ofText="de"
                  rowsText="filas"
                />
              </div>
            )}
          </div>
        </div>

        <CancelChargeModal
          charge={chargeToCancel}
          onClose={() => setChargeToCancel(null)}
          onCancelled={handleCancelled}
        />

        {isMobile && (
          <p className="text-muted text-center mt-2" style={{ fontSize: 12 }}>
            Columnas adicionales visibles en pantalla más grande
          </p>
        )}
      </div>
    </>
  );
};

export default PaymentsList;
